/**
 * useStaggerReveal — staggered grid entrance animation.
 *
 * Implements the specification from qam-animation-handoff.md & qam-animation-demos.html:
 * - Trigger: IntersectionObserver when entering viewport (or immediately if already in view).
 * - Motion: opacity 0→1 + translateY(28px)→0, ~600ms cubic-bezier(.16,.84,.44,1).
 * - Sequential stagger: ~50ms delay per card.
 * - One-shot execution: Unobserves after firing; never re-triggers on scroll back up.
 * - Re-arm support: When filter/sort/tab changes, pass `fresh: true` to re-stagger new items.
 * - Infinite scroll: When new pages are appended, newly added items are observed and animate as they scroll into view.
 * - Reduced motion: Collapses instantly to visible when prefers-reduced-motion is active.
 */
import { nextTick, type Ref, onUnmounted, ref } from 'vue';

function isReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useStaggerReveal() {
  const cardObserver = ref<IntersectionObserver | null>(null);
  const cardTimeouts = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

  const afterPaint = () =>
    new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );

  function clearCardTimer(el: HTMLElement) {
    const timer = cardTimeouts.get(el);
    if (timer) {
      clearTimeout(timer);
      cardTimeouts.delete(el);
    }
  }

  function clearAll() {
    cardTimeouts.forEach((timer) => clearTimeout(timer));
    cardTimeouts.clear();
    if (cardObserver.value) {
      cardObserver.value.disconnect();
      cardObserver.value = null;
    }
  }

  try {
    onUnmounted(() => {
      clearAll();
    });
  } catch {
    // Gracefully handle if called outside Vue setup context
  }

  function revealCard(el: HTMLElement, delay = 0) {
    if (el.dataset.staggered === 'true') return;
    el.dataset.staggered = 'true';

    clearCardTimer(el);

    if (isReducedMotion() || delay <= 0) {
      el.classList.add('in');
      return;
    }

    const timer = setTimeout(() => {
      el.classList.add('in');
      cardTimeouts.delete(el);
    }, delay);
    cardTimeouts.set(el, timer);
  }

  async function reveal(
    containerRef: Ref<HTMLElement | null> | HTMLElement | null,
    {
      selector = '.stagger-card, .stagger-item',
      delay = 50,
      fresh = false,
      threshold = 0.05,
    } = {}
  ) {
    await nextTick();
    await afterPaint();

    const containerEl =
      containerRef && typeof containerRef === 'object' && 'value' in containerRef
        ? containerRef.value
        : (containerRef as HTMLElement | null);
    if (!containerEl) return;

    const allItems = Array.from(
      containerEl.querySelectorAll<HTMLElement>(selector)
    );
    if (!allItems.length) return;

    // If fresh (e.g. filter/sort change or fresh load), reset only this container's items
    if (fresh) {
      allItems.forEach((el) => {
        clearCardTimer(el);
        if (cardObserver.value) {
          cardObserver.value.unobserve(el);
        }
        el.classList.remove('in');
        delete el.dataset.staggered;
      });
      void containerEl.offsetHeight; // Force reflow
    }

    const unStaggered = allItems.filter((el) => !el.dataset.staggered);
    if (!unStaggered.length) return;

    if (!cardObserver.value && typeof IntersectionObserver !== 'undefined') {
      cardObserver.value = new IntersectionObserver(
        (entries) => {
          const intersectingEntries = entries.filter((e) => e.isIntersecting);
          intersectingEntries.forEach((entry, idx) => {
            const targetEl = entry.target as HTMLElement;
            cardObserver.value?.unobserve(targetEl);
            const itemDelay = Math.min(idx * delay, 250);
            revealCard(targetEl, itemDelay);
          });
        },
        { threshold, rootMargin: '120px 0px 60px 0px' }
      );
    }

    const windowH = typeof window !== 'undefined' ? window.innerHeight : 1000;
    const currentlyInView: HTMLElement[] = [];
    const belowView: HTMLElement[] = [];

    unStaggered.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < windowH + 120 && rect.bottom > -60) {
        currentlyInView.push(el);
      } else {
        belowView.push(el);
      }
    });

    // Sort in-view items by reading order
    currentlyInView.sort((a, b) => {
      const rectA = a.getBoundingClientRect();
      const rectB = b.getBoundingClientRect();
      const rowA = Math.round(rectA.top / 15) * 15;
      const rowB = Math.round(rectB.top / 15) * 15;
      if (rowA !== rowB) return rowA - rowB;
      return rectA.left - rectB.left;
    });

    // Animate items already visible in the viewport
    currentlyInView.forEach((el, index) => {
      const itemDelay = Math.min(index * delay, 600);
      revealCard(el, itemDelay);
    });

    // Observe items below the viewport to animate right when scrolled into view
    belowView.forEach((el) => {
      if (cardObserver.value) {
        cardObserver.value.observe(el);
      } else {
        revealCard(el, 0);
      }
    });
  }

  return { reveal, clearAll };
}
